import { RunDto, RunResultsAggregate } from "@custom-types/modelRun"
import { color_tones, lighter_rgba, to_rgba } from "@features/results/Result/const"
import { getRunNamesMap } from "@utils/run"
import { ChartData, ChartOptions, TooltipItem } from "chart.js"
import { generateChartLabel } from "@utils/chartLabelUtils"

export type DataSetsMap = Map<string, Map<string, number[]>>

type DataSetMetadata = {
  nodeKey: string
  runKey?: string
}

export type DataSetsWithMetadata = Map<
  string,
  { values: Map<string, number[]>; metadata: DataSetMetadata }
>

type BuildTimeSeriesDataSetsProps = {
  aggregateResults?: RunResultsAggregate
  lagShift: number
  runs: RunDto[]
}

/**
 * Handles the .50 tie edge case when sample counts are converted to percentages.
 * This avoids systematic upward rounding bias from Math.round (e.g. 48.5 -> 49 and 51.5 -> 52),
 * which can visually produce 101% totals on stacked bars.
 */
const roundHalfToEven = (value: number): number => {
  const floorValue = Math.floor(value)
  const fraction = value - floorValue

  if (Math.abs(fraction - 0.5) < Number.EPSILON * 10) {
    return floorValue % 2 === 0 ? floorValue : floorValue + 1
  }

  return Math.round(value)
}

export const buildTimeSeriesDataSets = ({
  aggregateResults,
  lagShift,
  runs,
}: BuildTimeSeriesDataSetsProps): {
  timestepLabels: number[]
  dataSets: DataSetsWithMetadata
} => {
  if (!aggregateResults || Object.keys(aggregateResults).length === 0) {
    return { timestepLabels: [], dataSets: new Map() }
  }

  const timesteps = new Set<number>()
  const dataSets: DataSetsWithMetadata = new Map()

  Object.entries(aggregateResults).forEach(([runKey, nodesResults]) => {
    Object.entries(nodesResults).forEach(([nodeKey, nodeResult]) => {
      if (nodeResult.data_type !== "categorical") return

      const sortedTimestepKeys = Object.keys(nodeResult.category_count).sort().slice(lagShift)
      const valuesByLabel: Map<string, number[]> = new Map()

      sortedTimestepKeys.forEach((timestepKey, index) => {
        timesteps.add(Number(timestepKey))
        const categoryCounts = nodeResult.category_count[timestepKey] ?? {}

        Object.entries(categoryCounts).forEach(([category, count]) => {
          const labelValues = valuesByLabel.get(category) || []
          labelValues[index] = Number(count)
          valuesByLabel.set(category, labelValues)
        })
      })

      const metadata: DataSetMetadata = { nodeKey, runKey }
      const stackKey = runs.length > 1 ? `${runKey}_${nodeKey}` : nodeKey

      dataSets.set(stackKey, { values: valuesByLabel, metadata })
    })
  })

  const timestepLabels = Array.from(timesteps).sort((a, b) => a - b)
  return { timestepLabels, dataSets }
}

// TODO: This function should be refactored to reduce nested loops and improve readability.
export const buildTimeSeriesChartData = (
  dataSets: DataSetsWithMetadata,
  timestepLabels: number[],
  isStacked: boolean,
  showAsPercentage = true,
  runs: RunDto[] = [],
): ChartData<"bar", number[], number> => {
  const datasets: ChartData<"bar", number[], number>["datasets"] = []
  let dataSetIndex = 0
  const runNamesMap = getRunNamesMap(runs)
  const numberOfRuns = runs.length

  for (const [stackKey, { values, metadata }] of dataSets) {
    const allValues = Array.from(values.values())
    const totalsByTimestep = Array.from({ length: timestepLabels.length }, (_, index) =>
      allValues.reduce((sum, optionValues) => sum + (optionValues[index] || 0), 0),
    )

    const currentRun = runs.find((run) => run.key === metadata.runKey)
    const runName = currentRun ? runNamesMap[currentRun.key] : undefined
    const runVersion = currentRun?.version

    // Bar Strings Chart shows occurrence counts, so units are not relevant
    const nodeLabel = generateChartLabel({
      nodeName: metadata.nodeKey,
      runName,
      runVersion,
      numberOfRuns,
    })

    const sortedKeys = Array.from(values.keys()).sort()
    for (const internalKey of sortedKeys) {
      const internalValues = values.get(internalKey) || []

      const tone = color_tones[dataSetIndex % color_tones.length]
      if (!tone) {
        continue
      }

      const dataValues = showAsPercentage
        ? Array.from({ length: timestepLabels.length }, (_, index) => {
            const total = totalsByTimestep[index]
            if (!total) {
              return 0
            }
            return ((internalValues[index] || 0) / total) * 100
          })
        : Array.from({ length: timestepLabels.length }, (_, index) => internalValues[index] || 0)

      datasets.push({
        label: `${nodeLabel} ${internalKey}`,
        data: dataValues,
        borderColor: to_rgba(lighter_rgba(tone, 0, 0.8)),
        borderWidth: 1,
        backgroundColor: to_rgba(lighter_rgba(tone, 0, 0.7)),
        stack: isStacked ? stackKey : undefined,
      })

      dataSetIndex++
    }
  }

  return {
    labels: timestepLabels,
    datasets,
  }
}

export const createBarStringsChartOptions = (
  isStacked: boolean,
  timestepLabels: number[],
  showLabels: boolean,
): ChartOptions<"bar"> => ({
  plugins: {
    legend: {
      display: true,
      labels: {
        filter: (item: { text: string }) => Boolean(item.text),
      },
    },
    tooltip: {
      mode: "index",
      position: "average",
      callbacks: {
        label: (tooltipItem: TooltipItem<"bar">) => [
          tooltipItem.dataset.label || "",
          `${Number(tooltipItem.raw || 0).toFixed(2)}%`,
        ],
      },
    },
    datalabels: {
      display: showLabels,
      formatter: (value: number) => {
        if (!value) {
          return ""
        }
        return `${roundHalfToEven(value)}%`
      },
      color: "#fff",
      anchor: "center",
      align: "center",
    },
    zoom: {
      limits: {
        x: { minRange: 2 },
      },
      zoom: {
        drag: {
          enabled: true,
        },
        pinch: {
          enabled: true,
        },
        mode: "x",
      },
    },
  },
  responsive: true,
  scales: {
    x: {
      stacked: isStacked,
      min: timestepLabels[0] || undefined,
      max: timestepLabels[timestepLabels.length - 1] || undefined,
      ticks: {
        callback: (_value, index) => {
          const timestep = timestepLabels[index]
          if (timestep === undefined) {
            return ""
          }

          return String(timestep)
        },
      },
    },
    y: {
      stacked: isStacked,
      min: 0,
      max: 100,
      ticks: {
        callback: (value) => `${value}%`,
      },
    },
  },
})
